import { debounce as originDebounce } from '@rehaje/shared'

import { FatalDecoratorExistedError } from '../errors'
import { REF } from '../flags'
import { createRefDescriptor, ref } from './ref'

import type { DebounceAnnotation, DebounceOptions } from '../types'

export function createDebounceDescriptor(
  proto: any,
  key: string,
  options?: DebounceOptions
): PropertyDescriptor {
  const descriptor = createRefDescriptor(proto, key, options)
  return {
    ...descriptor,
    get(this) {
      return descriptor.get?.call(this)
    },
    set(this: any, value) {
      if (!this.__rr_debouncedSet) descriptor.set?.call(this, value)
      this.__rr_debouncedSet ??= originDebounce(
        descriptor.set,
        options?.wait,
        options
      )
      this.__rr_debouncedSet.call(this, value)
    }
  }
}

export const debounce: DebounceAnnotation<DebounceOptions> = (
  value,
  options
) => {
  const obs = ref(value, options)
  const debouncedSet = originDebounce(
    (value) => {
      obs.value = value
    },
    options?.wait,
    options
  )
  return {
    __rr_flags: REF,
    ...debouncedSet,
    get value() {
      return obs.value
    },
    set value(value) {
      debouncedSet(value)
    }
  }
}

debounce._name = 'debounce'

debounce._tsDecorator = (proto, key, descriptor, options) => {
  if (descriptor !== undefined)
    throw new FatalDecoratorExistedError(debounce._name, key)
  return createDebounceDescriptor(proto, key, options)
}

debounce._esAccessorDecorator = (context, options) => {
  const descriptor = createDebounceDescriptor(
    undefined,
    String(context.name),
    options
  )
  return {
    get: descriptor.get,
    init: descriptor.set,
    set: descriptor.set
  }
}
